import React, { useState, useContext, useEffect, useRef } from 'react';
import { BsStars } from 'react-icons/bs';
import { IoIosArrowForward } from 'react-icons/io';

import { LocaleContext } from '../layout';
import ContactBar from '../ContactBar/ContactBar';
import NavBar from '../NavBar/Navbar';
import NavBarMobile from '../NavBarMobile/NavBarMobile';

import * as styles from './NewHeader.module.scss';

function NewHeader({ data, flags }) {
  const [isCalendlyModalOpen, setIsCalendlyModalOpen] = useState(false);
  const [CalendlyPopupModal, setCalendlyPopupModal] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isHeaderHidden, setIsHeaderHidden] = useState(false);
  const [menuModalRightOffset, setMenuModalRightOffset] = useState(null);
  const headerRef = useRef(null);
  const menuButtonRef = useRef(null);
  const menuDialogRef = useRef(null);
  const menuBackdropRef = useRef(null);
  const wasMenuOpenRef = useRef(false);
  const inertTargetsRef = useRef([]);
  const lastScrollYRef = useRef(0);
  const scrollTickingRef = useRef(false);
  const heroElementRef = useRef(null);
  const headerHeightRef = useRef(0);
  const heroThresholdRef = useRef(0);

  const rootElement =
    typeof document !== 'undefined'
      ? document.getElementById('___gatsby')
      : null;

  const { locale } = useContext(LocaleContext);

  const headerData = data || {};

  const safeFlags = flags || { nodes: [] };

  const {
    contact_links,
    socials_title,
    social_links,
    logo_monstock,
    solution_group_name,
    needs_group_name,
    needs_group,
    sectors_group_name,
    sectors_group,
    links_group,
    body,
  } = headerData;

  const contactBarData = {
    contact_links,
    socials_title,
    social_links,
  };

  const navBarData = {
    logo_monstock,
    solution_group_name,
    needs_group_name,
    needs_group,
    sectors_group_name,
    sectors_group,
    links_group,
    body,
  };

  const iconsMap = {
    ia: BsStars,
    left: IoIosArrowForward,
  };

  const handleMenuLinkClick = () => {
    setIsMenuOpen(false);
  };

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    const dialog = menuDialogRef.current;
    if (!isMenuOpen || !dialog) {
      return;
    }

    const getFocusable = () =>
      Array.from(
        dialog.querySelectorAll(
          'a, button, summary, input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter(
        (element) =>
          !element.hasAttribute('disabled') &&
          element.getAttribute('aria-hidden') !== 'true'
      );

    const focusFirst = () => {
      const focusables = getFocusable();
      const first = focusables[0];
      if (first) {
        try {
          first.focus({ preventScroll: true });
        } catch (error) {
          first.focus();
        }
      }
    };

    focusFirst();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setIsMenuOpen(false);
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const focusables = getFocusable();
      if (focusables.length === 0) {
        event.preventDefault();
        try {
          dialog.focus({ preventScroll: true });
        } catch (error) {
          dialog.focus();
        }
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (!dialog.contains(active)) {
        event.preventDefault();
        first.focus({ preventScroll: true });
        return;
      }

      if (event.shiftKey) {
        if (active === first || !dialog.contains(active)) {
          event.preventDefault();
          last.focus({ preventScroll: true });
        }
        return;
      }

      if (active === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    };

    const handleFocusIn = (event) => {
      if (dialog.contains(event.target)) {
        return;
      }
      const focusables = getFocusable();
      const target = focusables[0] || dialog;
      try {
        target.focus({ preventScroll: true });
      } catch (error) {
        target.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('focusin', handleFocusIn);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('focusin', handleFocusIn);
    };
  }, [isMenuOpen]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const findHeroElement = () =>
      document.querySelector(
        '.section__hero, .hero-header, .herob-header, header.hero, section.hero'
      );

    const measureLayout = () => {
      headerHeightRef.current = headerRef.current?.offsetHeight || 0;

      let heroElement = heroElementRef.current;
      if (!heroElement || !heroElement.isConnected) {
        heroElement = findHeroElement();
        heroElementRef.current = heroElement;
      }

      if (!heroElement) {
        heroThresholdRef.current = headerHeightRef.current;
        return;
      }

      const heroBottom = heroElement.getBoundingClientRect().bottom + window.scrollY;
      heroThresholdRef.current = Math.max(0, heroBottom - headerHeightRef.current);
    };

    const handleWindowResize = () => {
      window.requestAnimationFrame(measureLayout);
    };

    measureLayout();
    window.addEventListener('resize', handleWindowResize, { passive: true });
    window.addEventListener('orientationchange', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
      window.removeEventListener('orientationchange', handleWindowResize);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    lastScrollYRef.current = window.scrollY || 0;

    const handleScroll = () => {
      if (scrollTickingRef.current) {
        return;
      }
      scrollTickingRef.current = true;

      window.requestAnimationFrame(() => {
        const currentY = window.scrollY || 0;
        const heroThreshold = heroThresholdRef.current;
        const delta = currentY - lastScrollYRef.current;
        const threshold = 8;

        if (isMenuOpen || currentY <= heroThreshold) {
          setIsHeaderHidden(false);
        } else if (delta > threshold) {
          setIsHeaderHidden(true);
        } else if (delta < -threshold) {
          setIsHeaderHidden(false);
        }

        lastScrollYRef.current = currentY;
        scrollTickingRef.current = false;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isMenuOpen]);

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      !isCalendlyModalOpen ||
      CalendlyPopupModal
    ) {
      return;
    }

    let isMounted = true;
    import('react-calendly').then((module) => {
      if (isMounted) {
        setCalendlyPopupModal(() => module.PopupModal);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [isCalendlyModalOpen, CalendlyPopupModal]);

  const restorePageInert = () => {
    inertTargetsRef.current.forEach(
      ({ element, ariaHidden, inert, inertAttr }) => {
        if (!element) return;
        if (ariaHidden === null) {
          element.removeAttribute('aria-hidden');
        } else {
          element.setAttribute('aria-hidden', ariaHidden);
        }
        if ('inert' in element) {
          element.inert = Boolean(inert);
        }
        if (!inertAttr) {
          element.removeAttribute('inert');
        }
      }
    );
    inertTargetsRef.current = [];
  };

  useEffect(() => {
    if (!isMenuOpen || typeof document === 'undefined') {
      return undefined;
    }

    const targets = new Set();
    const header = headerRef.current;
    const backdrop = menuBackdropRef.current;
    const menuButton = menuButtonRef.current;
    const menuButtonContainer = menuButton
      ? menuButton.closest('nav')
      : null;

    if (header) {
      Array.from(header.children).forEach((child) => {
        if (child !== backdrop) {
          if (menuButtonContainer && child.contains(menuButtonContainer)) {
            return;
          }
          targets.add(child);
        }
      });
    }

    const main = document.querySelector('main');
    if (main) {
      targets.add(main);
    }

    const footer = document.querySelector('footer');
    if (footer) {
      targets.add(footer);
    }

    inertTargetsRef.current = Array.from(targets).map((element) => ({
      element,
      ariaHidden: element.getAttribute('aria-hidden'),
      inert: 'inert' in element ? element.inert : null,
      inertAttr: element.hasAttribute('inert'),
    }));

    inertTargetsRef.current.forEach(({ element }) => {
      element.setAttribute('aria-hidden', 'true');
      if ('inert' in element) {
        element.inert = true;
      } else {
        element.setAttribute('inert', '');
      }
    });

    return restorePageInert;
  }, [isMenuOpen]);

  useEffect(() => {
    if (wasMenuOpenRef.current && !isMenuOpen) {
      menuButtonRef.current?.focus({ preventScroll: true });
    }
    wasMenuOpenRef.current = isMenuOpen;
  }, [isMenuOpen]);

  const handleBackdropClick = (event) => {
    if (event.target !== event.currentTarget) return;
    setIsMenuOpen(false);
  };

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const updateMenuAlignment = () => {
      const button = menuButtonRef.current;
      if (!button) return;
      const rect = button.getBoundingClientRect();
      const rightOffset = Math.max(0, window.innerWidth - rect.right);
      setMenuModalRightOffset(rightOffset);
    };

    updateMenuAlignment();
    window.addEventListener('resize', updateMenuAlignment, { passive: true });
    window.addEventListener('orientationchange', updateMenuAlignment);

    return () => {
      window.removeEventListener('resize', updateMenuAlignment);
      window.removeEventListener('orientationchange', updateMenuAlignment);
    };
  }, [isMenuOpen]);

  return (
    <header
      className={`${styles.header} ${
        isHeaderHidden ? styles.headerHidden : ''
      }`}
      ref={headerRef}
    >
      <ContactBar
        data={contactBarData}
        setIsCalendlyModalOpen={setIsCalendlyModalOpen}
      />
      <NavBar
        data={navBarData}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        menuButtonRef={menuButtonRef}
        flags={safeFlags}
        locale={locale}
      />
      <svg
        className={styles.menuModalContent_iconDefs}
        aria-hidden="true"
        focusable="false"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient
            id="menuModalIconGradient"
            x1="100%"
            y1="0%"
            x2="0%"
            y2="0%"
          >
            <stop offset="0%" stopColor="#FFA734" />
            <stop offset="100%" stopColor="#E7222D" />
          </linearGradient>
        </defs>
      </svg>

      {CalendlyPopupModal && (
        <CalendlyPopupModal
          url={`https://calendly.com/monstock/${
            locale === 'fr-fr' ? 'rdv' : 'book'
          }`}
          pageSettings={{
            backgroundColor: 'ffffff',
            hideEventTypeDetails: false,
            hideLandingPageDetails: false,
            primaryColor: '00a2ff',
            textColor: '4d5055',
            position: 'absolute',
            zIndex: 20000,
          }}
          // utm={utm}
          onModalClose={() => setIsCalendlyModalOpen(false)}
          open={isCalendlyModalOpen}
          /*
           * react-calendly uses React's Portal feature (https://reactjs.org/docs/portals.html) to render the popup modal. As a result, you'll need to
           * specify the rootElement property to ensure that the modal is inserted into the correct domNode.
           */
          rootElement={rootElement}
        />
      )}

      <div
        className={`${styles.menuModalBackdrop} ${
          !isMenuOpen ? styles.menuModalBackdropHidden : ''
        }`}
        onClick={handleBackdropClick}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleBackdropClick();
          }
        }}
        aria-hidden={!isMenuOpen}
        ref={menuBackdropRef}
      >
        <div
          className={`${styles.menuModal} ${
            !isMenuOpen ? styles.menuModalHidden : ''
          }`}
          style={
            menuModalRightOffset === null
              ? undefined
              : { '--menu-modal-right': `${menuModalRightOffset}px` }
          }
          role="dialog"
          id="mobile-menu"
          aria-modal="true"
          aria-labelledby="mobile-menu-title"
          aria-hidden={!isMenuOpen}
          ref={menuDialogRef}
          tabIndex={-1}
        >
          <NavBarMobile
            navBarData={navBarData}
            iconsMap={iconsMap}
            handleMenuLinkClick={handleMenuLinkClick}
            safeFlags={safeFlags}
            locale={locale}
          />
        </div>
      </div>
    </header>
  );
}

export default NewHeader;
